<template>
    <div v-if="isWidgetPage" class="free-delivery-widget-outer">
        <!-- Spacer in document flow when the banner is fixed sticky below header on scroll -->
        <div v-if="isFixedSticky" class="widget-flow-spacer" :style="{ height: `${widgetPlaceholderHeight}px` }"></div>

        <div
            ref="widgetWrapperRef"
            class="free-delivery-widget-wrapper align-top"
            :class="{ 'is-sticky-fixed': isFixedSticky }"
        >
            <!-- Collapsed Compact Floating Bar (Sticky when scrolling down / collapsed) -->
            <div v-if="isMinimized" class="compact-cart-pill shadow-lg cursor-pointer d-flex align-center justify-space-between" :class="{ 'unlocked-gold-glow': isUnlocked }" @click="toggleMinimize">
                <div class="compact-pill-content d-flex align-center flex-nowrap ga-1 ga-sm-2">
                    <button id="order-cart-icon-btn" class="cart-btn pa-1 flex-shrink-0" @click.stop="goToCart" :title="terms.cart || 'Cart'">
                        <v-badge :content="cartItemCount" :model-value="cartItemCount > 0" color="#ffc451" text-color="black"
                            offset-x="-4" offset-y="-4">
                            <v-icon color="#ffc451" class="cart-header-icon">mdi-cart-outline</v-icon>
                        </v-badge>
                    </button>
                    <div v-if="cartItemCount > 0" class="d-flex align-center ga-1 flex-shrink-0">
                        <span class="text-caption text-grey-lighten-1 d-none d-sm-inline">{{ terms.total || 'Total:' }}</span>
                        <span class="compact-price font-weight-bold price-accent-gold">
                            {{ formatNumber(finalTotalEuro) }}{{ terms.currency_euro || '€' }}
                        </span>
                        <span class="compact-secondary-currency text-caption text-grey-lighten-2">
                            ({{ formatNumber(finalTotalLeva) }}{{ terms.currency_leva || 'BGN' }})
                        </span>
                    </div>
                    <div v-else class="d-flex align-center ga-1 flex-shrink-0">
                        <span class="compact-price font-weight-medium text-grey-lighten-1">
                            {{ terms.cart_is_empty || terms.cart_empty || 'Cart is empty' }}
                        </span>
                    </div>
                    <span class="compact-divider text-grey-darken-1 flex-shrink-0">|</span>
                    <div class="compact-delivery d-flex align-center ga-1 flex-nowrap flex-shrink-0">
                        <v-icon :color="isUnlocked ? '#ffc451' : 'warning'" size="16">
                            {{ isUnlocked ? 'mdi-gift' : 'mdi-truck-fast-outline' }}
                        </v-icon>
                        <span class="compact-delivery-text font-weight-medium" :class="{ 'text-gold': isUnlocked }">
                            <template v-if="isUnlocked">
                                {{ terms.free_delivery || 'FREE DELIVERY' }}
                            </template>
                            <template v-else-if="cartItemCount === 0">
                                <span class="d-none d-sm-inline">
                                    {{ terms.free_delivery_over || 'Free delivery over ' }}{{ formatNumber(FREE_SHIPPING_THRESHOLD) }} {{ terms.currency_euro || '€' }}
                                </span>
                                <span class="d-inline d-sm-none">
                                    {{ terms.free_over || 'Free over ' }}{{ formatNumber(FREE_SHIPPING_THRESHOLD) }} {{ terms.currency_euro || '€' }}
                                </span>
                            </template>
                            <template v-else>
                                <span class="d-none d-sm-inline">
                                    {{ terms.left_prefix || 'Only ' }}{{ formatNumber(remainingAmount) }} {{ terms.currency_euro || '€' }} {{ terms.to_free_delivery || 'to free delivery' }}
                                </span>
                                <span class="d-inline d-sm-none">
                                    {{ terms.left_short_prefix || 'Left ' }}{{ formatNumber(remainingAmount) }} {{ terms.currency_euro || '€' }}
                                </span>
                            </template>
                        </span>
                    </div>
                </div>

                <button type="button" class="expand-pill-btn ml-1 d-flex align-center justify-center flex-shrink-0" @click.stop="toggleMinimize" :title="terms.expand || 'Expand'">
                    <v-icon color="#ffc451" size="16">mdi-chevron-down</v-icon>
                </button>
            </div>

            <!-- Full Expanded Floating Card (Full width of main frame 1200px) -->
            <div v-else class="free-delivery-card shadow-lg cursor-pointer" :class="{ 'unlocked-gold-glow': isUnlocked }" @click="toggleMinimize">
                <!-- Cart Info Header -->
                <div class="cart-summary-header d-flex align-center justify-space-between pb-2 mb-2">
                    <div class="d-flex align-center ga-1 ga-sm-2 flex-nowrap">
                        <button id="order-cart-icon-btn" class="cart-btn pa-1 mr-1 flex-shrink-0" @click.stop="goToCart" :title="terms.cart || 'Cart'">
                            <v-badge :content="cartItemCount" :model-value="cartItemCount > 0" color="#ffc451" text-color="black"
                                offset-x="-4" offset-y="-4">
                                <v-icon color="#ffc451" class="cart-header-icon">mdi-cart-outline</v-icon>
                            </v-badge>
                        </button>
                        <span class="cart-header-title font-weight-bold text-no-wrap">{{ terms.cart || 'Cart' }}</span>
                    </div>

                    <div class="d-flex align-center ga-1 ga-sm-2 flex-nowrap">
                        <div v-if="cartItemCount > 0" class="cart-header-price-pill px-2 px-sm-3 py-1 rounded-pill d-flex align-center flex-nowrap text-no-wrap">
                            <span class="price-sub-label text-caption mr-1 font-weight-medium text-grey-lighten-1 d-none d-sm-inline">{{ terms.total || 'Total:' }}</span>
                            <span class="price-accent-gold font-weight-bold mr-1 text-no-wrap">
                                {{ formatNumber(finalTotalEuro) }} {{ terms.currency_euro || 'EUR' }}
                            </span>
                            <span class="price-sub-leva text-caption d-none d-md-inline">
                                ({{ formatNumber(finalTotalLeva) }} {{ terms.currency_leva || 'BGN' }})
                            </span>
                        </div>
                        <div v-else class="cart-header-price-pill px-2 px-sm-3 py-1 rounded-pill d-flex align-center flex-nowrap text-no-wrap">
                            <span class="text-caption text-grey-lighten-1 font-weight-medium">
                                {{ terms.cart_is_empty || terms.cart_empty || 'Cart is empty' }}
                            </span>
                        </div>

                        <button type="button" class="expand-pill-btn ml-1 d-flex align-center justify-center flex-shrink-0" @click.stop="toggleMinimize" :title="terms.collapse || 'Collapse'">
                            <v-icon color="#ffc451" size="16">mdi-chevron-up</v-icon>
                        </button>
                    </div>
                </div>

                <!-- Empty Cart Note (when cartItemCount === 0) -->
                <div v-if="!isMinimized && cartItemCount === 0" class="widget-empty-note py-2 px-1 text-center text-grey-lighten-1 font-weight-medium">
                    <span>🛒 {{ terms.cart_empty_description || 'Your cart is empty. Add products to place an order.' }}</span>
                </div>

                <!-- Price Breakdown: Each on a new row with icon (only when cart has items) -->
                <div v-else-if="!isMinimized" class="widget-prices-list">
                    <!-- Subtotal Row -->
                    <div class="widget-subnote-text price-detail-row font-weight-medium">
                        <span>🛒 {{ terms.subtotal || terms.price || 'Subtotal: ' }}</span>
                        <span class="price-val-highlight font-weight-bold">
                            {{ formatNumber(cartTotalEuro) }}{{ terms.currency_euro || '€' }} | {{ formatNumber(cartTotalLeva) }}{{ terms.currency_leva || 'BGN' }}
                        </span>
                    </div>

                    <!-- Delivery Cost Row -->
                    <div class="widget-subnote-text price-detail-row mt-1 font-weight-medium">
                        <span>🚚 {{ terms.delivery_cost_label || 'Delivery: ' }}</span>
                        <span v-if="isUnlocked" class="text-success font-weight-bold">
                            🎉 {{ terms.free || 'FREE' }}
                        </span>
                        <span v-else-if="hasCalculatedCost" class="delivery-price-highlight font-weight-bold">
                            {{ calculatedDeliveryCost.euro === 0 ? (terms.free || 'FREE') : `${formatNumber(calculatedDeliveryCost.euro)}${terms.currency_euro || '€'} | ${formatNumber(calculatedDeliveryCost.leva)}${terms.currency_leva || 'BGN'}` }}
                        </span>
                        <span v-else-if="isOrderPage" class="text-grey font-italic">
                            {{ terms.order_devlivery_price_not_enough_data || 'Add delivery details to calculate shipping cost!' }}
                        </span>
                        <span v-else class="text-grey font-italic">
                            {{ terms.delivery_calculated_on_checkout || 'Calculated at checkout' }}
                        </span>
                    </div>

                    <!-- Final Total Price Row -->
                    <div class="widget-subnote-text price-detail-row final-total-row mt-2 pt-2 border-top-subtle font-weight-bold">
                        <span class="final-total-label">
                            💳 {{ terms.total || 'Total: ' }}
                        </span>
                        <span class="price-accent-gold final-total-value font-weight-bold">
                            {{ formatNumber(finalTotalEuro) }}{{ terms.currency_euro || '€' }} | {{ formatNumber(finalTotalLeva) }}{{ terms.currency_leva || 'BGN' }}
                        </span>
                    </div>
                </div>

                <!-- Delivery Status Notification & Progress Bar (Below Prices) -->
                <div v-if="!isMinimized" class="delivery-threshold-section mt-3 pt-2 border-top-subtle">
                    <div class="d-flex align-center justify-space-between mb-1 ga-2">
                        <div class="d-flex align-center ga-2">
                            <v-icon :color="isUnlocked ? '#ffc451' : 'warning'" size="18" class="widget-main-icon widget-icon">
                                {{ isUnlocked ? 'mdi-gift' : 'mdi-truck-fast-outline' }}
                            </v-icon>
                            <span class="widget-text font-weight-medium">
                                <template v-if="isUnlocked">
                                    <span class="gold-sparkle-text">
                                        <span class="text-no-wrap">🎉 {{ terms.congratulations || 'Congratulations!' }}</span>
                                        <span class="text-no-wrap ml-1">{{ terms.free_delivery || 'FREE DELIVERY!' }}</span>
                                        <template v-if="hasCalculatedCost && savedDeliveryEuro > 0">
                                            <span class="text-success font-weight-bold d-block mt-1" style="font-size: 0.82rem;">
                                                💰 {{ (terms.saved_delivery_amount || 'You save $AMOUNT$ on delivery!').replace('$AMOUNT$', `${formatNumber(savedDeliveryEuro)} ${terms.currency_euro || '€'} (${formatNumber(savedDeliveryLeva)} ${terms.currency_leva || 'BGN'})`) }}
                                            </span>
                                        </template>
                                    </span>
                                </template>
                                <template v-else-if="cartItemCount === 0">
                                    <span class="text-no-wrap">
                                        {{ terms.free_delivery_over || 'Free delivery over ' }}
                                        <strong class="price-accent">{{ formatNumber(FREE_SHIPPING_THRESHOLD) }} {{ terms.currency_euro || '€' }}</strong>!
                                    </span>
                                </template>
                                <template v-else>
                                    <span class="text-no-wrap mr-1">
                                        {{ terms.left_prefix || 'Only ' }}
                                        <strong class="price-accent">{{ formatNumber(remainingAmount) }} {{ terms.currency_euro || 'EUR' }}</strong>
                                    </span>
                                    <span class="text-no-wrap">
                                        {{ terms.left_for_free_delivery || 'left for free delivery' }}
                                    </span>
                                </template>
                            </span>
                        </div>
                    </div>

                    <!-- Animated Progress Bar -->
                    <div class="progress-bar-track mt-1">
                        <div class="progress-bar-fill" :style="{ width: `${progressPercent}%` }" :class="{ 'fill-unlocked': isUnlocked }"></div>
                    </div>
                </div>

                <!-- Checkout CTA Button (Shown on offers page when cart has items) -->
                <div v-if="!isMinimized && isOffersPage && cartItemCount > 0" class="widget-checkout-action mt-3">
                    <button type="button" class="widget-checkout-btn w-100 d-flex align-center justify-center ga-2 font-weight-bold" @click.stop="goToCart">
                        <v-icon size="18">mdi-cart-arrow-right</v-icon>
                        <span class="widget-checkout-btn-text">{{ terms.checkout || 'Checkout' }}</span>
                        <v-icon size="18">mdi-arrow-right</v-icon>
                    </button>
                </div>

                <!-- Large Quantity Custom Offer Note (shown only on cart page) -->
                <div v-if="!isMinimized && isOrderPage" class="widget-subnote-text mt-2 pt-1 font-weight-normal text-grey-lighten-1">
                    💡 {{ terms.large_quantity_contact_note || 'For large quantities, please contact us for a custom offer!' }}
                </div>
            </div>

            <!-- Confetti Particle Overlay Container -->
            <div id="confetti-container" class="confetti-container"></div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, inject, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';

const store = useStore();
const router = useRouter();
const route = useRoute();
const axios = inject('axios');

const terms = computed(() => store.state.terms);
const isBg = computed(() => store.state.languageCode === 'bg');
const cartItemCount = computed(() => store.getters.cartItemCount);
const cartTotalEuro = computed(() => store.getters.cartTotalEuro);
const cartTotalLeva = computed(() => store.getters.cartTotalLeva);
const isWidgetPage = computed(() => route.path === '/offers' || route.path === '/order');
const isOffersPage = computed(() => route.path === '/offers');
const isOrderPage = computed(() => route.path === '/order');
const calculatedDeliveryCost = computed(() => store.getters.calculatedDeliveryCost);
const hasCalculatedCost = computed(() => calculatedDeliveryCost.value?.isValid && calculatedDeliveryCost.value?.euro !== null && calculatedDeliveryCost.value?.euro !== undefined);

const FREE_SHIPPING_THRESHOLD = computed(() => store.getters.freeDeliveryThreshold);
const isMinimized = ref(false);

const remainingAmount = computed(() => {
    return Math.max(0, FREE_SHIPPING_THRESHOLD.value - cartTotalEuro.value);
});

const progressPercent = computed(() => {
    if (!FREE_SHIPPING_THRESHOLD.value) return 100;
    return Math.min(100, Math.round((cartTotalEuro.value / FREE_SHIPPING_THRESHOLD.value) * 100));
});

const isUnlocked = computed(() => {
    return cartTotalEuro.value >= FREE_SHIPPING_THRESHOLD.value;
});

const savedDeliveryEuro = computed(() => {
    if (hasCalculatedCost.value && calculatedDeliveryCost.value?.originalEuro && calculatedDeliveryCost.value.originalEuro > 0) {
        return calculatedDeliveryCost.value.originalEuro;
    }
    return 0;
});

const savedDeliveryLeva = computed(() => {
    if (hasCalculatedCost.value && calculatedDeliveryCost.value?.originalLeva && calculatedDeliveryCost.value.originalLeva > 0) {
        return calculatedDeliveryCost.value.originalLeva;
    }
    return 0;
});

const finalTotalEuro = computed(() => {
    if (isUnlocked.value) return Number(cartTotalEuro.value);
    const delivery = (hasCalculatedCost.value && calculatedDeliveryCost.value?.euro) ? calculatedDeliveryCost.value.euro : 0;
    return Number(cartTotalEuro.value) + Number(delivery);
});

const finalTotalLeva = computed(() => {
    if (isUnlocked.value) return Number(cartTotalLeva.value);
    const delivery = (hasCalculatedCost.value && calculatedDeliveryCost.value?.leva) ? calculatedDeliveryCost.value.leva : 0;
    return Number(cartTotalLeva.value) + Number(delivery);
});

const tryCalculateFromSavedSession = async () => {
    if (store.getters.calculatedDeliveryCost || !cartItemCount.value || !axios) return;
    try {
        const savedRaw = sessionStorage.getItem('avem_order_form_state');
        if (!savedRaw) return;
        const parsed = JSON.parse(savedRaw);
        const savedOrder = parsed?.order;
        if (!savedOrder || !savedOrder.city) return;

        const isOfficeDelivery = savedOrder.deliveryTo === 2; // DELIVERY_TO_OFFICE
        if (isOfficeDelivery && !savedOrder.office) return;

        const companyName = savedOrder.deliveryCompany === 2 ? 'econt' : 'speedy';
        const productsPayload = (store.getters.cartItems || []).map(item => ({ ...item.product, quantity: item.quantity }));
        if (!productsPayload.length) return;

        const payload = {
            products: productsPayload,
            city: savedOrder.city,
            office: savedOrder.office,
            isCOD: savedOrder.paymentWith === 1 // PAYMENT_COD
        };
        const response = await axios.post(`/${companyName}/delivery/calculate/${store.state.languageCode || 'bg'}`, payload);
        if (response.data?.isValid) {
            store.commit('setCalculatedDeliveryCost', {
                euro: response.data.costEuro,
                leva: response.data.costLeva,
                originalEuro: response.data.originalCostEuro || response.data.costEuro,
                originalLeva: response.data.originalCostLeva || response.data.costLeva,
                isValid: true
            });
        }
    } catch (e) {
        console.warn('Failed to calculate delivery from saved session in widget:', e);
    }
};

watch(
    () => cartItemCount.value,
    (count) => {
        if (count > 0 && !store.getters.calculatedDeliveryCost) {
            tryCalculateFromSavedSession();
        }
    },
    { immediate: true }
);

const widgetWrapperRef = ref(null);
const widgetPlaceholderHeight = ref(0);
const isFixedSticky = ref(false);
let isUserManuallyToggled = false;
let toggleScrollPosition = 0;

const updateWidgetPlaceholderHeight = () => {
    if (widgetWrapperRef.value && !isFixedSticky.value) {
        widgetPlaceholderHeight.value = widgetWrapperRef.value.offsetHeight;
    }
};

const onWindowScroll = () => {
    if (typeof window === 'undefined') return;
    const currentScrollY = window.scrollY;
    const isTopStickyMode = window.innerWidth < 1950;

    if (isTopStickyMode) {
        // When near top (<= 25px): expand and remain in flow
        if (currentScrollY <= 25) {
            isFixedSticky.value = false;
            isMinimized.value = false;
            isUserManuallyToggled = false;
            updateWidgetPlaceholderHeight();
        } 
        // When scrolling down past 35px: stick like header and collapse
        else if (currentScrollY > 35) {
            if (!isFixedSticky.value) {
                updateWidgetPlaceholderHeight();
                isFixedSticky.value = true;
            }
            if (isUserManuallyToggled) {
                if (Math.abs(currentScrollY - toggleScrollPosition) > 150) {
                    isMinimized.value = true;
                    isUserManuallyToggled = false;
                }
            } else {
                isMinimized.value = true;
            }
        }
    } else {
        isFixedSticky.value = false;
    }
};

onMounted(() => {
    if (typeof window !== 'undefined') {
        onWindowScroll();
        window.addEventListener('scroll', onWindowScroll, { passive: true });
        window.addEventListener('resize', onWindowScroll, { passive: true });
        setTimeout(updateWidgetPlaceholderHeight, 200);
        tryCalculateFromSavedSession();
    }
});

onUnmounted(() => {
    if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', onWindowScroll);
        window.removeEventListener('resize', onWindowScroll);
    }
});

const formatNumber = (val) => {
    if (val === undefined || val === null || isNaN(val)) return '0,00';
    return Number(val).toFixed(2).replace('.', ',');
};

const goToCart = () => {
    router.push('/order');
};

const toggleMinimize = () => {
    isMinimized.value = !isMinimized.value;
    isUserManuallyToggled = true;
    if (typeof window !== 'undefined') {
        toggleScrollPosition = window.scrollY;
    }
};

// Celebration Confetti Burst Animation
const triggerCelebrationConfetti = () => {
    const container = document.getElementById('confetti-container');
    if (!container) return;

    const colors = ['#ffc451', '#ffd584', '#e53935', '#4caf50', '#2196f3', '#9c27b0', '#ffffff'];
    const particleCount = 45;

    for (let i = 0; i < particleCount; i++) {
        const confetti = document.createElement('div');
        confetti.className = 'confetti-particle';

        const color = colors[Math.floor(Math.random() * colors.length)];
        const size = Math.floor(Math.random() * 8) + 6;
        const left = Math.random() * 100; // Percentage across viewport
        const animDuration = (Math.random() * 1.5 + 1.2).toFixed(2); // 1.2s - 2.7s
        const rotation = Math.floor(Math.random() * 720) - 360;

        confetti.style.backgroundColor = color;
        confetti.style.width = `${size}px`;
        confetti.style.height = size > 9 && Math.random() > 0.5 ? `${size * 2}px` : `${size}px`;
        confetti.style.borderRadius = Math.random() > 0.5 ? '50%' : '2px';
        confetti.style.left = `${left}vw`;
        confetti.style.top = `-20px`;
        confetti.style.opacity = '1';
        confetti.style.animation = `confettiFall ${animDuration}s cubic-bezier(0.25, 1, 0.5, 1) forwards`;
        confetti.style.transform = `rotate(${rotation}deg)`;

        container.appendChild(confetti);

        setTimeout(() => {
            if (confetti.parentNode) {
                confetti.parentNode.removeChild(confetti);
            }
        }, parseFloat(animDuration) * 1000 + 100);
    }
};

// Watch cart total to trigger celebration animation when crossing 100 EUR threshold
watch(
    () => cartTotalEuro.value,
    (newVal, oldVal) => {
        const threshold = FREE_SHIPPING_THRESHOLD.value;
        if ((oldVal || 0) < threshold && newVal >= threshold) {
            triggerCelebrationConfetti();
        }
    }
);
</script>

<style scoped>
/* Outer container */
.free-delivery-widget-outer {
    width: 100%;
    margin-bottom: 8px; /* Matches the 8px space above the widget */
    position: relative;
    animation: widgetFadeIn 0.25s ease-out;
}

@keyframes widgetFadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

/* Base Banner Layout (< 1950px: Capped at max-width 600px, sticks on top below header) */
.free-delivery-widget-wrapper {
    position: relative;
    width: 100%;
    max-width: 600px !important;
    margin: 0 auto !important;
    display: flex;
    justify-content: center;
    padding: 8px 16px 0 16px;
    pointer-events: auto;
    background: transparent !important;
    backdrop-filter: none !important;
    border: none !important;
    box-shadow: none !important;
    box-sizing: border-box;
    bottom: auto !important;
    transition: top 0.2s ease, opacity 0.2s ease;
}

/* Sticky on top below header when scrolling down */
.free-delivery-widget-wrapper.is-sticky-fixed {
    position: fixed !important;
    top: 72px !important;
    bottom: auto !important;
    left: 0 !important;
    right: 0 !important;
    margin: 0 auto !important;
    max-width: 600px !important;
    width: 100% !important;
    z-index: 990 !important;
    padding: 8px 16px !important;
}

@media (min-width: 600px) and (max-width: 959px) {
    .free-delivery-widget-wrapper {
        padding: 6px 12px 0 12px;
        bottom: auto !important;
    }
    .free-delivery-widget-wrapper.is-sticky-fixed {
        top: 60px !important;
        bottom: auto !important;
        padding: 6px 12px !important;
    }
}

@media (max-width: 599px) {
    .free-delivery-widget-outer {
        margin-bottom: 6px; /* Matches the 6px space above the widget on mobile */
    }
    .free-delivery-widget-wrapper {
        padding: 6px 8px 0 8px;
        bottom: auto !important;
    }
    .free-delivery-widget-wrapper.is-sticky-fixed {
        top: 52px !important;
        bottom: auto !important;
        padding: 6px 8px !important;
    }
}

/* Ultra-Wide Screens (>= 1950px: Floats in the right gutter outside the main 1200px frame) */
@media (min-width: 1950px) {
    .free-delivery-widget-outer {
        margin-bottom: 0 !important;
    }

    .free-delivery-widget-wrapper {
        position: fixed !important;
        top: 92px !important;
        left: calc(50vw + 600px) !important;
        right: auto !important;
        bottom: auto !important;
        z-index: 990 !important;
        width: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        max-width: calc(50vw - 616px) !important;
        display: block !important;
        background: transparent !important;
        backdrop-filter: none !important;
        border: none !important;
    }

    .free-delivery-card {
        width: 440px !important;
        max-width: 440px !important;
        border-radius: 20px !important;
        padding: 16px 20px !important;
        box-shadow: 0 12px 35px rgba(0, 0, 0, 0.55) !important;
    }

    .compact-cart-pill {
        width: 500px !important;
        max-width: 500px !important;
    }
}

.cart-summary-header {
    border-bottom: 1px solid rgba(255, 196, 81, 0.25);
}

.compact-cart-pill {
    background: rgba(20, 20, 20, 0.95);
    color: #ffffff;
    border: 1.5px solid rgba(255, 196, 81, 0.6);
    border-radius: 9999px;
    width: 100%;
    max-width: 600px;
    padding: 8px 18px 8px 12px;
    min-height: 48px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.65);
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    backdrop-filter: blur(12px);
    box-sizing: border-box;
    white-space: nowrap;
}

.compact-pill-content {
    min-width: 0;
    overflow: hidden;
}

.compact-cart-pill:hover {
    border-color: #ffc451;
    box-shadow: 0 8px 25px rgba(255, 196, 81, 0.3);
}

.compact-cart-icon {
    width: 30px !important;
    height: 30px !important;
}

.compact-price {
    color: #ffc451;
    font-size: 0.88rem;
    letter-spacing: 0.2px;
}

.compact-divider {
    font-size: 0.85rem;
    opacity: 0.45;
}

.compact-delivery-text {
    font-size: 0.82rem;
    color: #e0e0e0;
}

.text-gold {
    color: #ffc451 !important;
    font-weight: 700;
}

.expand-pill-btn {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(255, 196, 81, 0.15);
    border: none;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease;
}

.expand-pill-btn:hover {
    background: rgba(255, 196, 81, 0.35);
    transform: scale(1.1);
}

.cart-btn {
    background: transparent;
    border: none;
    padding: 2px 4px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;
}

.cart-btn:hover {
    transform: scale(1.15);
}

.cart-btn :deep(.v-badge__badge) {
    font-size: 11px !important;
    font-weight: 800 !important;
    height: 14px !important;
    min-width: 14px !important;
    padding: 0 2px !important;
    border-radius: 50% !important;
    line-height: 14px !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4) !important;
}

.cart-header-icon {
    font-size: 22px !important;
}

.cart-header-title {
    color: #ffffff;
    font-size: 0.95rem;
    line-height: 1.2;
}

.cart-header-price-pill {
    background: rgba(255, 196, 81, 0.12);
    border: 1px solid rgba(255, 196, 81, 0.4);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.price-accent-gold {
    color: #ffc451;
    font-size: 0.95rem;
}

.price-sub-leva {
    color: #d5d5d5;
    font-size: 0.8rem;
}

.free-delivery-card {
    background: #151515;
    color: #ffffff;
    border: 1.5px solid rgba(255, 196, 81, 0.6);
    border-radius: 16px;
    width: 100%;
    max-width: 600px;
    padding: 14px 20px !important;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.65);
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    backdrop-filter: blur(12px);
    box-sizing: border-box;
}

.price-detail-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0;
    font-size: 0.92rem;
    color: #e0e0e0;
}

.price-val-highlight {
    color: #ffffff;
}

.final-total-row {
    font-size: 1rem;
    color: #ffffff;
}

.final-total-value {
    font-size: 1.15rem;
    letter-spacing: 0.3px;
}

.border-top-subtle {
    border-top: 1px solid rgba(255, 196, 81, 0.25) !important;
}

@media (min-width: 1400px) {
    .free-delivery-card {
        max-width: 450px !important;
        border-radius: 20px !important;
        padding: 16px 20px !important;
        box-shadow: 0 12px 35px rgba(0, 0, 0, 0.55) !important;
    }

    .compact-cart-pill {
        max-width: 450px !important;
        width: auto !important;
    }
}

.compact-cart-pill {
    background: rgba(20, 20, 20, 0.95);
    color: #ffffff;
    border: 1.5px solid rgba(255, 196, 81, 0.6);
    border-radius: 9999px;
    width: 100%;
    max-width: 1200px;
    padding: 6px 18px 6px 12px;
    box-shadow: 0 6px 22px rgba(0, 0, 0, 0.55);
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    backdrop-filter: blur(12px);
    white-space: nowrap;
}

.widget-price-breakdown-row {
    border-top: 1px solid rgba(255, 196, 81, 0.2);
}

.widget-text {
    font-size: 0.85rem !important;
    line-height: 1.35;
    letter-spacing: 0.1px;
}

.widget-empty-note {
    font-size: 0.85rem;
    line-height: 1.4;
}

.unlocked-gold-glow {
    border-color: #ffc451 !important;
    box-shadow: 0 12px 40px rgba(255, 196, 81, 0.55), 0 0 20px rgba(255, 196, 81, 0.35) !important;
    animation: goldPulse 2s infinite ease-in-out;
}

@keyframes goldPulse {
    0%, 100% {
        box-shadow: 0 12px 40px rgba(255, 196, 81, 0.55), 0 0 20px rgba(255, 196, 81, 0.35);
    }
    50% {
        box-shadow: 0 15px 50px rgba(255, 196, 81, 0.8), 0 0 30px rgba(255, 196, 81, 0.6);
    }
}

.price-accent {
    color: #ffc451;
    font-size: 0.88rem !important;
    font-weight: 700;
}

.gold-sparkle-text {
    color: #ffc451;
    text-shadow: 0 0 10px rgba(255, 196, 81, 0.7);
}

.progress-bar-track {
    width: 100%;
    height: 10px;
    background: rgba(255, 255, 255, 0.18);
    border-radius: 12px;
    overflow: hidden;
    position: relative;
}

.progress-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #d89e2c 0%, #ffc451 100%);
    border-radius: 12px;
    transition: width 0.6s cubic-bezier(0.25, 1, 0.5, 1);
}

.fill-unlocked {
    background: linear-gradient(90deg, #ffc451 0%, #fff 50%, #ffc451 100%) !important;
    background-size: 200% 100% !important;
    animation: shimmerFill 1.5s infinite linear;
}

@keyframes shimmerFill {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
}

.widget-subnote-text {
    font-size: 0.85rem !important;
    line-height: 1.35;
    color: rgba(255, 255, 255, 0.85);
    border-top: 1px solid rgba(255, 255, 255, 0.15);
}

.close-widget-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    border-radius: 50%;
    transition: background 0.2s, transform 0.2s;
}

.close-widget-btn:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: scale(1.1);
}

.widget-main-icon {
    font-size: 28px !important;
}

.close-widget-icon {
    font-size: 20px !important;
}

.widget-icon {
    transition: transform 0.3s ease;
}

.delivery-price-highlight {
    color: #ffc451;
    font-weight: 700;
}

.free-delivery-card:hover .widget-icon {
    transform: scale(1.15) rotate(-5deg);
}

/* Tablet Responsiveness (600px - 959px) */
@media (min-width: 600px) and (max-width: 959px) {
    .widget-text {
        font-size: 0.88rem !important;
        line-height: 1.3;
    }

    .price-accent {
        font-size: 0.95rem;
        font-weight: 700;
    }

    .widget-main-icon {
        font-size: 22px !important;
    }

    .close-widget-icon {
        font-size: 16px !important;
    }

    .progress-bar-track {
        height: 7px;
    }

    .widget-subnote-text {
        font-size: 0.75rem !important;
        line-height: 1.25;
    }
}

/* Phone Responsiveness (< 600px) */
@media (max-width: 599px) {
    .compact-cart-pill {
        padding: 6px 14px 6px 10px !important;
        min-height: 46px !important;
        border-radius: 9999px !important;
    }

    .compact-cart-icon {
        width: 32px !important;
        height: 32px !important;
        margin-right: 2px !important;
    }

    .compact-price {
        font-size: 0.86rem !important;
    }

    .compact-secondary-currency {
        font-size: 0.72rem !important;
        opacity: 0.8;
    }

    .compact-delivery-text {
        font-size: 0.80rem !important;
    }

    .compact-divider {
        margin: 0 3px !important;
    }

    .widget-text {
        font-size: 0.74rem !important;
        line-height: 1.25;
    }

    .price-accent {
        font-size: 0.78rem !important;
        font-weight: 700;
    }

    .cart-header-icon {
        font-size: 20px !important;
    }

    .cart-header-title {
        font-size: 0.82rem !important;
        white-space: nowrap !important;
    }

    .cart-header-price-pill {
        padding: 3px 8px !important;
        font-size: 0.80rem !important;
        white-space: nowrap !important;
    }

    .price-accent-gold {
        font-size: 0.84rem !important;
        white-space: nowrap !important;
    }

    .price-detail-row {
        font-size: 0.80rem !important;
        padding: 2px 0 !important;
    }

    .final-total-row {
        font-size: 0.88rem !important;
    }

    .final-total-value {
        font-size: 0.95rem !important;
    }

    .widget-main-icon {
        font-size: 18px !important;
    }

    .close-widget-icon {
        font-size: 14px !important;
    }

    .progress-bar-track {
        height: 5px;
        border-radius: 6px;
    }

    .widget-subnote-text {
        font-size: 0.68rem !important;
        line-height: 1.2;
        padding-top: 2px !important;
    }
}

/* Confetti Container */
.confetti-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    pointer-events: none;
    z-index: 999999;
    overflow: hidden;
}

@keyframes confettiFall {
    0% {
        top: -20px;
        opacity: 1;
        transform: rotate(0deg) translateX(0);
    }
    100% {
        top: 105vh;
        opacity: 0;
        transform: rotate(720deg) translateX(50px);
    }
}
/* Checkout CTA Button */
.widget-checkout-btn {
    background: linear-gradient(135deg, #ffc451 0%, #ffac1c 100%) !important;
    color: #1a1a1a !important;
    font-size: 0.92rem !important;
    font-weight: 700 !important;
    letter-spacing: 0.5px;
    padding: 10px 18px !important;
    border-radius: 14px !important;
    border: none !important;
    cursor: pointer !important;
    box-shadow: 0 4px 15px rgba(255, 196, 81, 0.4) !important;
    transition: all 0.22s ease-in-out !important;
    text-transform: uppercase;
}

.widget-checkout-btn:hover {
    background: linear-gradient(135deg, #ffd071 0%, #ffb833 100%) !important;
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(255, 196, 81, 0.55) !important;
}

.widget-checkout-btn:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(255, 196, 81, 0.3) !important;
}

.widget-checkout-btn-text {
    font-size: 0.92rem;
    font-weight: 800;
}
</style>
